import {
	hideLoadingScreen,
	router,
	setPageContent,
	showLoadingScreen,
} from "..";
import { deleteFencealarm, putFencealarm } from "../api/fencealarms_{imei}";
import { getPrices } from "../api/prices";
import {
	nameField,
	ownerField,
	sharingField,
	subscriptionField,
	tagsField,
} from "../device/device_settings_fields";
import { openUnregisterDeviceModal } from "../device/unregister_device_modal";
import { locale } from "../locale";
import { state } from "../state";
import { Bulma } from "../view/bulma";
import { centeredBox, errorToast } from "../view/components";

/**
 * @type {import('navigo').Handler}
 */
async function fencealarmSettingsPage(ctx) {
	if (!ctx || !ctx.data || !ctx.data.imei) throw "missing ctx.data.imei";
	const fa = state.fencealarms[ctx.data.imei];

	const user = await state.getActiveUser();
	const currency = user.Country === "SE" ? "SEK" : "EUR";
	const isOwner = user.Email === fa.Metadata.OwnerEmail;

	const { FencealarmMonth, FencealarmYear } = await getPrices();

	const voltageAlarm = voltageAlarmField(fa);
	const calibration = calibrationField(fa);
	const subscription = subscriptionField(
		fa.Subscription,
		[FencealarmMonth, FencealarmYear],
		currency,
		isOwner,
	);
	const sharing = sharingField(fa.Metadata);
	const tags = tagsField(fa.Metadata);
	const name = nameField(fa.Metadata.Name);
	const owner = ownerField(fa.Metadata, isOwner);

	const save = () => {
		showLoadingScreen();
		putFencealarm(fa.Imei, {
			VoltageAlarm: {
				Enabled: voltageAlarm.isEnabled(),
				AlarmLevel: voltageAlarm.getThreshold(),
			},
			Calibration: {
				Enabled: calibration.isEnabled(),
				ExpectedVoltage: calibration.getVoltage(),
			},
			PriceId: subscription.getPriceId(),
			Name: name.getValue(),
			Access: sharing.getAccess(),
			Notify: sharing.getNotify(),
			Tags: tags.getValue(),
			Owner: owner.getValue(),
		})
			.then((fa) => {
				state.fencealarms[fa.Imei] = fa;
				router.jump(`/devices?id=${fa.Imei}`);
			})
			.catch(errorToast)
			.finally(hideLoadingScreen);
	};

	const unregister = () => {
		showLoadingScreen(),
			deleteFencealarm(fa.Imei)
				.then(() => {
					delete state.fencealarms[fa.Imei];
					router.jump("/devices");
				})
				.catch(errorToast)
				.finally(hideLoadingScreen);
	};

	setPageContent(
		centeredBox(
			"small",
			Bulma.title(`FenceAlarm ${fa.Metadata.Name}`, "is-4"),
			Bulma.subtitle(`IMEI: ${fa.Imei}`, "is-6"),
			voltageAlarm.element,
			calibration.element,
			subscription.element,
			sharing.element,
			tags.element,
			name.element,
			owner.element,
			Bulma.buttons(
				Bulma.button(
					{
						onClick: save,
						styles: ["is-primary"],
					},
					locale.strings.save,
				),
				Bulma.button(
					{
						onClick: () => router.jump(`/devices?id=${fa.Imei}`),
						styles: ["is-primary"],
					},
					locale.strings.back,
				),
				Bulma.button(
					{
						disabled: !isOwner,
						onClick: () =>
							openUnregisterDeviceModal("FenceAlarm", unregister),
						styles: ["is-danger"],
					},
					locale.strings.unregister,
				),
			),
		),
	);
}

/**
 * @param {Fencealarm} fa
 */
function voltageAlarmField(fa) {
	const isSubscribed =
		fa.Subscription.PriceIds.Current !== "" ||
		Date.now() < new Date(fa.Subscription.ExpiresAt).getTime();

	const onOffSelect = Bulma.select({
		disabled: !isSubscribed,
		options: [
			Bulma.option(
				locale.strings.on,
				"on",
				isSubscribed && fa.VoltageAlarm.Enabled,
			),
			Bulma.option(
				locale.strings.off,
				"off",
				!isSubscribed || !fa.VoltageAlarm.Enabled,
			),
		],
	});

	/** @type {(level: number) => HTMLOptionElement} */
	const thresholdOption = (threshold) =>
		Bulma.option(
			`${threshold} V`,
			threshold.toString(),
			threshold === fa.Config.AlarmLevel,
		);

	const thresholdSelect = Bulma.select({
		options: [
			thresholdOption(2000),
			thresholdOption(3000),
			thresholdOption(4000),
			thresholdOption(5000),
			thresholdOption(6000),
			thresholdOption(7000),
			thresholdOption(8000),
		],
	});

	const element = Bulma.field(
		{},
		Bulma.label(
			locale.string({
				enGB: "Voltage alarm",
				deDE: "Spannungsalarm",
				frFR: "Alarme de tension",
				svSE: "Spänningslarm",
			}),
		),
		Bulma.field(
			{
				styles: ["is-grouped"],
			},
			Bulma.control({}, onOffSelect.element),
			Bulma.control({}, thresholdSelect.element),
		),
	);

	return {
		element,
		isEnabled: () => onOffSelect.getValue() === "on",
		getThreshold: () => +thresholdSelect.getValue(),
	};
}

/**
 * @param {Fencealarm} fa
 */
function calibrationField(fa) {
	const onOffSelect = Bulma.select({
		options: [
			Bulma.option(
				locale.strings.on,
				"on",
				fa.Calibration.ScalingFactor > 1,
			),
			Bulma.option(
				locale.strings.off,
				"off",
				fa.Calibration.ScalingFactor === 1,
			),
		],
	});

	const expectedVoltage = Bulma.input({
		type: "text",
		placeholder: locale.string({
			enGB: "Expected voltage",
			deDE: "Erwartete Spannung",
			frFR: "Tension attendue",
			svSE: "Förväntad spänning",
		}),
		value:
			fa.Calibration.ScalingFactor > 1
				? fa.Calibration.ExpectedVoltage.toString()
				: "",
	});

	const field = Bulma.field(
		{},
		Bulma.label(
			locale.string({
				enGB: "Calibration",
				deDE: "Kalibrierung",
				frFR: "Calibrage",
				svSE: "Kalibrering",
			}),
		),
		Bulma.field(
			{
				styles: ["is-grouped"],
			},
			Bulma.control({}, onOffSelect.element),
			Bulma.control({}, expectedVoltage.element),
		),
	);

	return {
		element: field,
		isEnabled: () => onOffSelect.getValue() === "on",
		getVoltage: () => +expectedVoltage.getValue(),
	};
}

export { fencealarmSettingsPage };
